import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { setSeo, track } from "@/lib/api";

const CATEGORIES = [
  "Home Saunas",
  "Golf Simulators",
  "Massage Chairs",
  "Outdoor Kitchens",
  "Cold Plunges",
  "Standby / Whole-Home Generators",
  "Adjustable Beds",
  "Louvered Pergolas",
  "Home Battery Backup",
  "Home-Theater Projectors",
];

const FLOW = [
  "Question",
  "Quick useful answer",
  "Relevant product path",
  "Optional help",
  "Deeper research",
];

const FOUNDATION = [
  ["12 Decision Guides", "Researched guides built around the questions homeowners actually ask."],
  ["Sourced price guidance", "Cost-focused guides carry dated price ranges with their sources attached."],
  ["Five planning tools", "Room fit, project planning, generator coverage and sleep and theater selectors."],
  ["Verified research records", "Factual claims are recorded with their source, scope and verification date."],
  ["Product-discovery paths", "Clear routes to relevant options, built to support merchant integration later."],
  ["Editorial standards", "Factual claims are researched, sourced and verified before publication."],
];

const PRINCIPLES = [
  "Relevance before commission",
  "Verified information before promotion",
  "Product fit before popularity",
  "Clear trade-offs instead of universal winners",
];

const PARTNER_VALUE = [
  "High-consideration home-purchase categories",
  "Decision-focused editorial content",
  "Price and fit questions close to purchase intent",
  "Clear product pathways",
  "Educational context before outbound clicks",
  "Long-form evergreen search opportunities",
  "Product and category deep-link compatibility as integrations are added",
];

const GOOD_FIT = [
  "Clear product information",
  "Transparent pricing where practical",
  "Dependable fulfillment",
  "Strong product-detail pages",
  "Accurate specifications",
  "Clear warranty and return information",
  "Deep-link capability where available",
  "Catalogs that can be matched naturally to homeowner needs",
];

const ContactCta = ({ testId }) => (
  <Link
    to="/contact?topic=partnership"
    data-testid={testId}
    className="inline-flex items-center gap-2 px-9 py-[17px] bg-charcoal text-white text-[13px] tracking-[0.16em] uppercase hover:bg-sage transition-colors duration-300"
  >
    Contact Human Home Living <ArrowRight size={15} />
  </Link>
);

const Section = ({ eyebrow, heading, intro, children, tone = "light", testId }) => (
  <section
    data-testid={testId}
    className={`px-5 md:px-8 py-16 md:py-24 ${tone === "dark" ? "bg-charcoal" : "bg-transparent"}`}
  >
    <div className="max-w-[1080px] mx-auto">
      {eyebrow && (
        <span className={`eyebrow ${tone === "dark" ? "eyebrow-light" : ""}`}>{eyebrow}</span>
      )}
      <h2
        className={`display text-[30px] md:text-[40px] leading-[1.15] mt-5 max-w-3xl ${
          tone === "dark" ? "text-white" : "text-charcoal"
        }`}
      >
        {heading}
      </h2>
      {intro && (
        <p
          className={`text-[17px] md:text-[19px] font-light leading-[1.6] mt-6 max-w-2xl ${
            tone === "dark" ? "text-[#C9C6C0]" : "text-[#4F4F4F]"
          }`}
        >
          {intro}
        </p>
      )}
      {children}
    </div>
  </section>
);

export default function Partners() {
  useEffect(() => {
    setSeo({
      title: "Partner With Human Home Living | Affiliate & Merchant Partnerships",
      description:
        "Learn about Human Home Living, the homeowner decision and product-discovery platform, our editorial approach, category coverage and partnership opportunities.",
    });
    track({ event_type: "page_view", page: "/partners" });
  }, []);

  return (
    <div data-testid="partners-page">
      {/* HERO */}
      <header className="bg-white border-b border-line px-5 md:px-8 pt-16 pb-16 md:pb-20">
        <div className="max-w-[1080px] mx-auto">
          <span className="eyebrow">Partner with Human Home Living</span>
          <h1 className="display text-[38px] md:text-[56px] leading-[1.04] mt-5 text-charcoal max-w-4xl">
            Help homeowners make confident decisions about significant home purchases.
          </h1>
          <p className="text-[18px] md:text-[22px] font-light leading-[1.5] text-[#4F4F4F] mt-7 max-w-2xl">
            Human Home Living is a homeowner decision and product-discovery platform focused on major
            purchases for the home. We combine practical guidance, verified research and clear paths to
            relevant product options so visitors can understand what fits before they buy.
          </p>
          <p className="text-[16px] font-light leading-[1.6] text-[#5F5B55] mt-5 max-w-2xl">
            Our focus is simple: answer the questions homeowners actually ask, explain the important
            trade-offs and make it easy to explore relevant options when they are ready.
          </p>
          <div className="mt-10">
            <ContactCta testId="partners-hero-cta" />
          </div>
        </div>
      </header>

      {/* COVERAGE */}
      <Section
        testId="partners-coverage"
        eyebrow="Category coverage"
        heading="Significant home purchases, organized around real homeowner decisions"
        intro="Human Home Living focuses on categories where price, fit, installation and household needs can materially change the right choice."
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line mt-10 border border-line">
          {CATEGORIES.map((c) => (
            <div key={c} className="bg-white px-6 py-6" data-testid={`partner-category-${c.length}`}>
              <span className="text-[16px] text-charcoal">{c}</span>
            </div>
          ))}
        </div>
        <p className="text-[13px] font-light text-[#6E6A63] mt-5 max-w-2xl">
          Category coverage reflects our editorial focus. Merchant inventory is not live in every
          category, and we do not list merchants as partners unless a relationship exists.
        </p>
      </Section>

      {/* SHOPPER EXPERIENCE */}
      <Section
        testId="partners-experience"
        tone="dark"
        eyebrow="The visitor experience"
        heading="Designed around the shortest useful path to a decision"
        intro="Human Home Living does not require visitors to complete unnecessary questionnaires, planning tools or long guides before seeing useful information."
      >
        <div className="grid md:grid-cols-5 gap-px bg-white/15 mt-10">
          {FLOW.map((step, i) => (
            <div key={step} className="bg-charcoal px-5 py-6 border border-white/10">
              <div className="text-[11px] tracking-[0.18em] text-[#A9B8A2]">0{i + 1}</div>
              <div className="text-[15px] text-white mt-2.5 leading-snug">{step}</div>
            </div>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-8 mt-10 max-w-4xl">
          <p className="text-[15.5px] font-light leading-[1.7] text-[#C9C6C0]">
            When a homeowner already knows enough to shop, we make the route to relevant options
            obvious. When they need help first, we provide concise guidance, planning tools or deeper
            research — without turning any of those steps into gates.
          </p>
          <p className="text-[15.5px] font-light leading-[1.7] text-[#C9C6C0]">
            The site is built to reduce friction, not to manufacture engagement. A visitor who gets the
            answer they need and reaches relevant options quickly is a successful visit.
          </p>
        </div>
      </Section>

      {/* FOUNDATION */}
      <Section
        testId="partners-foundation"
        eyebrow="Publishing foundation"
        heading="A research-backed publishing foundation"
        intro="Our platform includes researched Decision Guides, source-backed price guidance, homeowner planning tools and product-discovery architecture designed to support future merchant integrations without changing the visitor experience."
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line mt-10 border border-line">
          {FOUNDATION.map(([title, body]) => (
            <div key={title} className="bg-white px-6 py-7">
              <h3 className="text-[16.5px] font-medium text-charcoal">{title}</h3>
              <p className="text-[14px] font-light leading-[1.6] text-[#6A665F] mt-2">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* EDITORIAL */}
      <Section
        testId="partners-editorial"
        eyebrow="Editorial approach"
        heading="Editorial independence comes first"
      >
        <div className="prose-hhl mt-8 max-w-3xl">
          <p>
            A product can be excellent and still be the wrong choice for a particular home. Human Home
            Living looks beyond ratings and popularity to consider factors such as fit, dimensions,
            installation requirements, electrical requirements, features, ongoing costs and household
            needs.
          </p>
          <p>
            Affiliate relationships do not determine whether a product is appropriate or the conclusions
            in our guidance.
          </p>
          <p>
            When commercial relationships are present, we identify them as appropriate so visitors can
            understand when the site may receive compensation.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-px bg-line mt-10 border border-line max-w-3xl">
          {PRINCIPLES.map((p) => (
            <div key={p} className="bg-white px-6 py-5 text-[15px] text-charcoal">
              {p}
            </div>
          ))}
        </div>
      </Section>

      {/* FOUNDER */}
      <Section
        testId="partners-founder"
        eyebrow="Publisher background"
        heading="Built by an experienced home-services business owner and digital marketer"
      >
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 mt-8 items-start">
          <div className="prose-hhl">
            <p>
              Human Home Living was founded by Ernest Staton Jr., a longtime home-services business owner
              with a B.A. in Graphic Arts Management. He has operated Mr. Resurface LLC since 2001 and has
              worked professionally in bathtub resurfacing and reglazing since 1996.
            </p>
            <p>
              That work has meant serving homeowners directly, building and managing a business website,
              search-driven customer acquisition, digital advertising, lead generation and online
              marketing — the same disciplines this platform is built on.
            </p>
          </div>

          <div className="border border-line bg-white px-6 md:px-8 py-8" data-testid="partners-evidence">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#6E6A63]">
              Documented digital experience
            </span>
            <p className="text-[15.5px] font-light leading-[1.7] text-[#3F3F3F] mt-4">
              His established home-services website has generated more than 140,000 recorded site views
              and more than 3,900 documented leads, according to Townsquare Interactive reporting.
            </p>
            <p className="text-[15.5px] font-light leading-[1.7] text-[#3F3F3F] mt-4">
              Search accounted for 65% of recorded traffic, reflecting hands-on experience building
              search-driven customer acquisition.
            </p>
            <p className="text-[12px] font-light leading-[1.6] text-[#6E6A63] mt-6 pt-5 border-t border-line">
              These figures refer to MrResurface.com, the founder's prior and ongoing home-services
              business — not to Human Home Living. Source: Townsquare Interactive reporting for
              MrResurface.com, reviewed 6 September 2026.
            </p>
          </div>
        </div>
      </Section>

      {/* PARTNER VALUE */}
      <Section
        testId="partners-value"
        eyebrow="For partners"
        heading="A useful environment for high-consideration products"
        intro="Human Home Living is designed for categories where homeowners research cost, fit, installation and features before choosing a product. That creates natural opportunities for relevant merchants to appear at the point where a visitor understands the trade-offs and is ready to explore options."
      >
        <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-3.5 mt-10 max-w-3xl">
          {PARTNER_VALUE.map((v) => (
            <li key={v} className="text-[15.5px] font-light text-[#3F3F3F] pl-4 border-l border-sage">
              {v}
            </li>
          ))}
        </ul>
      </Section>

      {/* GOOD FIT */}
      <Section testId="partners-fit" eyebrow="Partner fit" heading="What makes a strong fit">
        <p className="text-[17px] font-light leading-[1.6] text-[#4F4F4F] mt-6 max-w-2xl">
          We are most interested in merchants and manufacturers that offer:
        </p>
        <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-3.5 mt-8 max-w-3xl">
          {GOOD_FIT.map((v) => (
            <li key={v} className="text-[15.5px] font-light text-[#3F3F3F] pl-4 border-l border-line">
              {v}
            </li>
          ))}
        </ul>
        <p className="text-[15.5px] font-light text-[#5F5B55] mt-8 max-w-2xl">
          We prefer partnerships that make the shopping experience more useful rather than more
          complicated.
        </p>
      </Section>

      {/* AFFILIATE RELATIONSHIPS */}
      <Section
        testId="partners-affiliate"
        eyebrow="Disclosure"
        heading="How affiliate relationships fit into Human Home Living"
      >
        <div className="prose-hhl mt-8 max-w-3xl">
          <p>
            Human Home Living may earn commissions through affiliate partnerships with retailers and
            manufacturers. These relationships do not determine which products we consider appropriate or
            the conclusions in our guidance.
          </p>
          <p>
            Commercial relationships are disclosed as appropriate, and we aim to keep editorial guidance
            separate from compensation. Our full{" "}
            <Link to="/affiliate-disclosure" className="link-underline text-sage-dark" data-testid="partners-disclosure-link">
              affiliate disclosure
            </Link>{" "}
            and{" "}
            <Link to="/editorial-standards" className="link-underline text-sage-dark" data-testid="partners-standards-link">
              editorial standards
            </Link>{" "}
            are published for anyone to review.
          </p>
        </div>
      </Section>

      {/* CONTACT */}
      <section data-testid="partners-contact" className="px-5 md:px-8 pb-20 md:pb-28">
        <div className="max-w-[1080px] mx-auto border-t-2 border-sage bg-white px-6 md:px-10 py-12 md:py-14">
          <h2 className="display text-[28px] md:text-[36px] leading-tight text-charcoal max-w-2xl">
            Interested in working with Human Home Living?
          </h2>
          <p className="text-[16.5px] font-light leading-[1.6] text-[#4F4F4F] mt-5 max-w-2xl">
            We welcome conversations with retailers, manufacturers and affiliate-program managers whose
            products fit the categories Human Home Living covers.
          </p>
          <div className="mt-9">
            <ContactCta testId="partners-contact-cta" />
          </div>
        </div>
      </section>
    </div>
  );
}
